import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { useLang, tField } from "../lib/LanguageContext";
import { api, imageUrl } from "../lib/api";
import SEO from "../components/SEO";

const CATEGORIES = [
    { key: "all", label_key: "all" },
    { key: "rooms", label_key: "cat_rooms" },
    { key: "beach", label_key: "cat_beach" },
    { key: "restaurant", label_key: "cat_restaurant" },
    { key: "hotel", label_key: "cat_hotel" },
    { key: "videos", label_key: "cat_videos" },
];

const FALLBACK = [
    { id: "f1", image: "seed:gallery_beach", category: "beach" },
    { id: "f2", image: "seed:gallery_pool", category: "hotel" },
    { id: "f3", image: "seed:gallery_exterior", category: "hotel" },
    { id: "f4", image: "seed:room_standard", category: "rooms" },
    { id: "f5", image: "seed:room_gabina", category: "rooms" },
    { id: "f6", image: "seed:room_apartment", category: "rooms" },
    { id: "f7", image: "seed:facility_restaurant", category: "restaurant" },
    { id: "f8", image: "seed:facility_bar", category: "restaurant" },
];

export default function Gallery() {
    const { t, lang } = useLang();
    const { site } = useOutletContext() || {};
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(null);

    useEffect(() => {
        api.get("/gallery")
            .then((r) => setItems(r.data?.length ? r.data : FALLBACK))
            .catch(() => setItems(FALLBACK));
    }, []);

    const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

    return (
        <div data-testid="gallery-page">
            <SEO
                title={lang === "al" ? "Galeria" : "Gallery"}
                description={lang === "al" ? "Fotografi nga Kompleksi Dea Radhimë — dhomat, plazhi, restoranti dhe ambientet e hotelit." : "Photos from Kompleksi Dea Radhimë — rooms, beach, restaurant and hotel ambience."}
                path="/gallery"
            />
            {/* Header */}
            <section className="relative h-[45vh] min-h-[340px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${imageUrl(site?.gallery_hero_image, imageUrl("seed:gallery_pool"))})`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-14 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">
                            {t("nav_gallery")}
                        </span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {t("gallery_title")}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {t("gallery_intro")}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Filters */}
            <section className="py-12 md:py-16 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-10 md:mb-14">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setFilter(c.key)}
                            data-testid={`filter-${c.key}`}
                            className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-all border ${
                                filter === c.key
                                    ? "bg-[#0F3A59] text-white border-[#0F3A59]"
                                    : "bg-transparent text-[#0F3A59] border-[#0F3A59]/20 hover:bg-[#0F3A59]/5"
                            }`}
                        >
                            {t(c.label_key)}
                        </button>
                    ))}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((g, i) => (
                            <motion.button
                                key={g.id}
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                                onClick={() => setOpen(g)}
                                data-testid={`gallery-item-${g.id}`}
                                className={`gallery-item rounded-xl overflow-hidden cursor-pointer relative ${
                                    i % 7 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"
                                }`}
                            >
                                <img decoding="async" loading="lazy"
                                    src={imageUrl(g.image)}
                                    alt={tField(g, "caption", lang)}
                                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                                />
                                {g.video_url && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                                        <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                                            <Play size={22} className="text-[#0F3A59] ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                )}
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </div>

                {filtered.length === 0 && (
                    <div className="text-center text-[#1C1C1C]/60 font-light py-20">
                        {t("no_data")}
                    </div>
                )}
            </section>

            {/* Lightbox */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(null)}
                        className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6"
                        data-testid="lightbox"
                    >
                        <button
                            onClick={() => setOpen(null)}
                            className="absolute top-6 right-6 text-white/80 hover:text-white"
                            aria-label="close"
                        >
                            <X size={32} strokeWidth={1.5} />
                        </button>
                        {open.video_url ? (
                            <div
                                className="w-full max-w-5xl aspect-video"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {open.video_url.includes("youtube.com") || open.video_url.includes("youtu.be") ? (
                                    <iframe
                                        src={open.video_url
                                            .replace("watch?v=", "embed/")
                                            .replace("youtu.be/", "youtube.com/embed/")}
                                        className="w-full h-full rounded-lg"
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen
                                        title="video"
                                    />
                                ) : (
                                    <video
                                        src={open.video_url.startsWith("http") ? open.video_url : imageUrl(open.video_url)}
                                        controls
                                        autoPlay
                                        className="w-full h-full rounded-lg bg-black"
                                    />
                                )}
                            </div>
                        ) : (
                            <motion.img
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                src={imageUrl(open.image)}
                                alt=""
                                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
